import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ClipboardList, ArrowLeft, Search, RefreshCw, DollarSign,
  ChevronLeft, ChevronRight, CheckCircle, Clock, XCircle, 
  AlertTriangle, Store, User, QrCode, Ban, Undo2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

import { API_URL } from '../../utils/apiConfig';

const CANCELLATION_REASONS = [
  { value: 'sender_requested', label: 'Sender requested cancellation' },
  { value: 'duplicate_authorization', label: 'Duplicate authorization' },
  { value: 'beneficiary_info_incorrect', label: 'Beneficiary information incorrect' },
  { value: 'merchant_unavailable', label: 'Merchant unavailable' },
  { value: 'fraud_security', label: 'Fraud/security concern' },
  { value: 'created_in_error', label: 'Authorization created in error' },
  { value: 'uat_testing', label: 'UAT/testing' },
  { value: 'other', label: 'Other (specify in notes)' }
];

export default function AdminAuthorizationsView() {
  const [loading, setLoading] = useState(true);
  const [authorizations, setAuthorizations] = useState([]);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 50;
  const navigate = useNavigate();

  // Cancel dialog state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedAuth, setSelectedAuth] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelNotes, setCancelNotes] = useState('');
  const [cancelling, setCancelling] = useState(false);

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchAuthorizations = useCallback(async () => {
    const token = getToken();
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams({
        status_filter: statusFilter,
        limit: limit.toString(),
        offset: offset.toString()
      });
      if (search) params.append('search', search);

      const response = await fetch(`${API_URL}/api/admin/authorizations?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 403) {
          navigate('/admin');
          return;
        }
        throw new Error('Failed to fetch authorizations');
      }

      const data = await response.json();
      setAuthorizations(data.authorizations);
      setTotal(data.total);
      setSummary(data.summary);
    } catch (error) {
      console.error('Error fetching authorizations:', error);
      toast.error('Failed to load authorizations');
    } finally {
      setLoading(false);
    }
  }, [navigate, isAdmin, statusFilter, offset, search]);

  useEffect(() => {
    fetchAuthorizations();
  }, [fetchAuthorizations]);

  const handleSearch = (e) => {
    e.preventDefault();
    setOffset(0);
    fetchAuthorizations();
  };

  const openCancelDialog = (auth) => {
    setSelectedAuth(auth);
    setCancelReason('');
    setCancelNotes('');
    setCancelDialogOpen(true);
  };

  const handleCancelRelease = async () => {
    if (!selectedAuth || !cancelReason) {
      toast.error('Please select a cancellation reason');
      return;
    }

    if (cancelReason === 'other' && !cancelNotes.trim()) {
      toast.error('Please provide notes for "Other" reason');
      return;
    }

    const token = getToken();
    setCancelling(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/authorizations/${selectedAuth.id}/cancel-release`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: cancelReason,
          notes: cancelNotes || null,
          idempotency_key: `cancel-${selectedAuth.id}-${Date.now()}`
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to cancel authorization');
      }

      toast.success(data.message || 'Authorization cancelled and funds released');
      setCancelDialogOpen(false);
      fetchAuthorizations();
    } catch (error) {
      console.error('Error cancelling authorization:', error);
      toast.error(error.message || 'Failed to cancel authorization');
    } finally {
      setCancelling(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-blue-900/50 text-blue-300 border-blue-700',
      authorized_pending_redemption: 'bg-blue-900/50 text-blue-300 border-blue-700',
      partially_redeemed: 'bg-purple-900/50 text-purple-300 border-purple-700',
      completed: 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
      fully_redeemed: 'bg-emerald-900/50 text-emerald-300 border-emerald-700',
      cancelled: 'bg-red-900/50 text-red-300 border-red-700',
      expired: 'bg-orange-900/50 text-orange-300 border-orange-700',
      expired_partial: 'bg-orange-900/50 text-orange-300 border-orange-700',
      expired_unredeemed: 'bg-orange-900/50 text-orange-300 border-orange-700',
      pending: 'bg-amber-900/50 text-amber-300 border-amber-700',
      failed: 'bg-red-900/50 text-red-300 border-red-700'
    };
    return styles[status] || 'bg-slate-700 text-slate-300 border-slate-600';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
      case 'fully_redeemed':
        return <CheckCircle className="w-3 h-3" />;
      case 'cancelled':
      case 'failed':
        return <XCircle className="w-3 h-3" />;
      case 'expired':
      case 'expired_partial':
      case 'expired_unredeemed':
        return <AlertTriangle className="w-3 h-3" />;
      default:
        return <Clock className="w-3 h-3" />;
    }
  };

  const isCancellable = (auth) => {
    const cancellableStatuses = ['active', 'authorized_pending_redemption', 'partially_redeemed', 'pending'];
    return cancellableStatuses.includes(auth.status) && (auth.remaining_amount_usd || 0) > 0;
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Authorizations</h1>
              <p className="text-sm text-slate-400">Manage purchase authorizations</p>
            </div>
          </div>
          <Button
            onClick={fetchAuthorizations}
            variant="outline"
            className="border-slate-600 text-slate-300"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-blue-400 mb-1">
                <Clock className="w-4 h-4" />
                <span className="text-xs">Active</span>
              </div>
              <p className="text-xl font-bold text-white">{summary.active_count || 0}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-purple-400 mb-1">
                <ClipboardList className="w-4 h-4" />
                <span className="text-xs">Partial</span>
              </div>
              <p className="text-xl font-bold text-white">{summary.partially_redeemed_count || 0}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-emerald-400 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-xs">Completed</span>
              </div>
              <p className="text-xl font-bold text-white">{summary.completed_count || 0}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-red-400 mb-1">
                <XCircle className="w-4 h-4" />
                <span className="text-xs">Cancelled</span>
              </div>
              <p className="text-xl font-bold text-white">{summary.cancelled_count || 0}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-orange-400 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-xs">Expired</span>
              </div>
              <p className="text-xl font-bold text-white">{summary.expired_count || 0}</p>
            </div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-400 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-xs">Outstanding</span>
              </div>
              <p className="text-xl font-bold text-white">${(summary.total_outstanding_usd || 0).toFixed(0)}</p>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4 mb-6">
          <form onSubmit={handleSearch} className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search by ID, beneficiary, phone, email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setOffset(0); }}>
              <SelectTrigger className="w-[180px] bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="partially_redeemed">Partially Redeemed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
            <Button type="submit" className="bg-purple-600 hover:bg-purple-700">
              <Search className="w-4 h-4 mr-2" />
              Search
            </Button>
          </form>
        </div>

        {/* Authorizations Table */}
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
            </div>
          ) : authorizations.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No authorizations found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">ID</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Beneficiary</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Merchant</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Amount</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Expires</th>
                    <th className="text-left text-xs font-medium text-slate-400 uppercase tracking-wider px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {authorizations.map((auth) => (
                    <motion.tr
                      key={auth.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-slate-700/30"
                    >
                      <td className="px-4 py-3">
                        <p className="text-white font-mono text-sm">{auth.id.slice(0, 8)}...</p>
                        {auth.reference_id && (
                          <p className="text-slate-500 text-xs">{auth.reference_id}</p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-500" />
                          <div className="text-sm">
                            <p className="text-slate-300">{auth.beneficiary_name || '-'}</p>
                            <p className="text-slate-500 text-xs">{auth.beneficiary_email || auth.beneficiary_phone || '-'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Store className="w-4 h-4 text-slate-500" />
                          <p className="text-slate-300 text-sm">{auth.merchant_business_name || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="text-white font-medium">${(auth.original_amount_usd || 0).toFixed(2)}</p>
                          <p className="text-slate-500 text-xs">
                            Remaining: <span className={auth.remaining_amount_usd > 0 ? 'text-amber-400' : 'text-slate-500'}>
                              ${(auth.remaining_amount_usd || 0).toFixed(2)}
                            </span>
                          </p>
                          {auth.already_redeemed_usd > 0 && (
                            <p className="text-slate-500 text-xs">
                              Redeemed: <span className="text-emerald-400">${(auth.already_redeemed_usd || 0).toFixed(2)}</span>
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs border ${getStatusBadge(auth.status)}`}>
                          {getStatusIcon(auth.status)}
                          {auth.status?.replace(/_/g, ' ')}
                        </span>
                        {auth.qr_status && (
                          <div className="flex items-center gap-1 text-slate-500 text-xs mt-1">
                            <QrCode className="w-3 h-3" />
                            QR: {auth.qr_status}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-slate-400 text-sm">
                        {auth.authorization_expires_at 
                          ? new Date(auth.authorization_expires_at).toLocaleDateString() 
                          : '-'}
                      </td>
                      <td className="px-4 py-3">
                        {isCancellable(auth) ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openCancelDialog(auth)}
                            className="border-red-700 text-red-400 hover:bg-red-900/30"
                          >
                            <Ban className="w-3 h-3 mr-1" />
                            Cancel & Release
                          </Button>
                        ) : auth.status === 'completed' || auth.status === 'fully_redeemed' ? (
                          <span className="text-xs text-slate-500">Fully redeemed</span>
                        ) : auth.status === 'cancelled' ? (
                          <span className="text-xs text-slate-500">Already cancelled</span>
                        ) : (
                          <span className="text-xs text-slate-500">-</span>
                        )}
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="border-slate-600"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="border-slate-600"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Cancel & Release Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <Ban className="w-5 h-5" />
              Cancel Authorization & Release Funds?
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action will stop future redemption and release unused funds back to the sender.
            </DialogDescription>
          </DialogHeader>

          {selectedAuth && (
            <div className="space-y-4">
              {/* Authorization Summary */}
              <div className="bg-slate-900 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400">Authorization:</span>
                  <span className="text-white font-mono">{selectedAuth.id.slice(0, 12)}...</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Original Amount:</span>
                  <span className="text-white">${(selectedAuth.original_amount_usd || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Already Redeemed:</span>
                  <span className="text-emerald-400">${(selectedAuth.already_redeemed_usd || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-slate-700 pt-2">
                  <span className="text-slate-400">Unused Amount to Release:</span>
                  <span className="text-amber-400 font-bold">${(selectedAuth.remaining_amount_usd || 0).toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Beneficiary:</span>
                  <span className="text-white">{selectedAuth.beneficiary_name || '-'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400">Merchant:</span>
                  <span className="text-white">{selectedAuth.merchant_business_name || '-'}</span>
                </div>
              </div>

              {/* Warning */}
              <div className="bg-amber-900/20 border border-amber-800 rounded-lg p-3 text-sm">
                <p className="text-amber-300">This action will:</p>
                <ul className="text-amber-200 text-xs mt-1 space-y-1 list-disc list-inside">
                  <li>Stop future redemption</li>
                  <li>Invalidate active QR/access credentials</li>
                  <li>Preserve completed purchases</li>
                  <li>Release only the unused authorization amount back to the sender</li>
                </ul>
                <p className="text-amber-400 text-xs mt-2 font-medium">
                  This action cannot be used to reverse already completed purchases.
                </p>
              </div>

              {/* Reason */}
              <div className="space-y-2">
                <Label className="text-slate-300">Cancellation Reason *</Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {CANCELLATION_REASONS.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label className="text-slate-300">
                  Notes {cancelReason === 'other' && <span className="text-red-400">*</span>}
                </Label>
                <Textarea
                  value={cancelNotes}
                  onChange={(e) => setCancelNotes(e.target.value)}
                  placeholder="Optional notes (required if reason is 'Other')"
                  className="bg-slate-700 border-slate-600 text-white"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setCancelDialogOpen(false)}
              className="border-slate-600"
            >
              Keep Authorization
            </Button>
            <Button
              onClick={handleCancelRelease}
              disabled={cancelling || !cancelReason}
              className="bg-red-600 hover:bg-red-700"
            >
              {cancelling ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Undo2 className="w-4 h-4 mr-2" />
                  Cancel & Release ${(selectedAuth?.remaining_amount_usd || 0).toFixed(2)}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
